import type { McpProbeResult, McpProbeStage } from '@ralph/contracts'
import type { Locale } from '@/i18n/locale.ts'
import type { SystemsPack } from './system-labels.ts'

/**
 * Turning one probe answer into six verdicts.
 *
 * The server reports a single stage — the first one that failed — and that is the right wire
 * shape: it stops at the first fatal problem and does not pretend to know about the ones
 * after it. What a reader needs is the whole ladder, because the *shape* of the answer is
 * half the diagnosis. "Address fine, reachable fine, protocol failed" and "address fine,
 * unreachable, protocol not reached" send somebody to two different teams, and a single red
 * line saying "failed" sends them to neither.
 *
 * So the rule is: **a stage is marked passed only where the probe actually established it.**
 * The failing one fails, and everything else — below the failure, and in the gap between
 * what was established and where the failure is displayed — is *not reached*, never
 * "failed". A step nobody attempted has no verdict, and inventing either colour for it is a
 * second, fictional problem for somebody to go and investigate.
 *
 * The first version of this module derived "established" from ladder position, which reads
 * as the same rule and is not: two of the eight wire stages fail out of display order, and
 * both were reported as having answers from a system that had never been contacted. See
 * `STAGE_OF`.
 *
 * Kept as data with no React in it because it is the part worth testing: the mapping from
 * eight wire stages onto six reader-facing ones has exactly the kind of off-by-one that a
 * screenshot will not catch — a ladder of confident green ticks looks equally convincing
 * when every one of them is invented.
 *
 * Every word it renders now comes from the catalogue, handed in as `t` (PRD-27 LOC-012).
 * This module has no component body to call `useI18n()` from, and an import would be a
 * module-scope catalogue read — the failure that renders the previous language with no type
 * error, no runtime error and no failing test (F-3). `components/memory/memory-grouping.ts`
 * is the in-repo model for the shape.
 */

/**
 * Just enough of the active locale for the one line on this ladder that counts something.
 *
 * Structural rather than a `Pick<I18n, …>`, so a pure module stays free of React's types.
 * `useI18n()` satisfies it as it stands.
 */
export type Counting = { locale: Locale; formatCount: (value: number) => string }

export type StageKey = 'address' | 'reach' | 'protocol' | 'identity' | 'authorization' | 'offer'

export const STAGE_ORDER: StageKey[] = ['address', 'reach', 'protocol', 'identity', 'authorization', 'offer']

export type StageState = 'pass' | 'fail' | 'pending' | 'skipped'

export type StageView = {
  key: StageKey
  state: StageState
  /** One line of evidence, in the reader's terms. Empty when there is nothing to say yet. */
  detail: string
}

/**
 * Where a wire stage lands, and how far the probe had actually got when it did.
 *
 * `attempted` is the count of display stages the probe genuinely established before this
 * failure, and it is a **separate number from the position** on purpose. Deriving one from
 * the other is the obvious shortcut and it is wrong twice:
 *
 *  - **`token_unresolved` happens before a socket is opened.** The probe resolves a
 *    credential reference locally, between checking the address and dialling, so a failure
 *    there means the system was never contacted. Reading its position as "everything above
 *    passed" produced `answered in 2 ms` about a server nobody had spoken to.
 *  - **`authorization_required` arrives before the system says anything about itself.** A
 *    401 establishes that the endpoint answered, and nothing whatever about which revision
 *    it speaks or what it is called. Marking identity as passed — with the words "did not
 *    say what it is called" — claims we asked and it declined.
 *
 * Both were the same mistake: a ladder position is where a reader *looks* for an answer, not
 * evidence that the question was put. The invariant this table has to keep is
 * `attempted <= indexOf(at)`, and the spec asserts it over every entry.
 *
 * Two wire stages collapse onto `address` and two onto `authorization`. The `address` pair
 * is a genuine pair — a malformed URL and a refused one are one question answered two ways.
 * The `authorization` pair is not, which is exactly why they carry different `attempted`
 * counts: one is a local fact about this deployment, the other is the far side's answer.
 */
type WireStage = { at: StageKey; attempted: number }

const STAGE_OF: Record<McpProbeStage, WireStage> = {
  url_invalid: { at: 'address', attempted: 0 },
  ssrf_blocked: { at: 'address', attempted: 0 },
  // Local, and before the dial: only the address had been established.
  token_unresolved: { at: 'authorization', attempted: 1 },
  connect: { at: 'reach', attempted: 1 },
  version_mismatch: { at: 'protocol', attempted: 2 },
  discover: { at: 'protocol', attempted: 2 },
  // It answered. That is all a challenge tells us.
  authorization_required: { at: 'authorization', attempted: 2 },
  tools_list: { at: 'offer', attempted: 5 }
}

/**
 * Whether a failure is a dead end or a fork in the flow.
 *
 * `authorization_required` is the one stage where "failed" is the wrong word for the reader.
 * The system answered, correctly, that it wants somebody to sign in — which is the next
 * question the flow was going to ask anyway. Rendering it as a failure teaches an
 * administrator that a working system is broken.
 */
export const isAnswerable = (stage: McpProbeStage | null): boolean => stage === 'authorization_required'

const passDetail = (t: SystemsPack, fmt: Counting, key: StageKey, result: McpProbeResult, host: string): string => {
  const evidence = t.flow.evidence
  if (key === 'address') return host
  if (key === 'reach') return evidence.answered(fmt, result.latencyMs)
  if (key === 'protocol') {
    // Unreachable by construction — this stage is only ever a pass once a revision has been
    // agreed, which is what `STAGE_OF` now encodes. Kept, and kept *empty*, because the
    // alternative when a future mapping changes is a sentence asserting an agreement that
    // did not happen, and silence is the one wrong answer that cannot mislead.
    if (!result.protocolVersion) return ''
    // Two entries rather than a suffix concatenated onto the version: the parenthetical is a
    // sentence, and a language whose word order differs cannot place it by string addition.
    return result.era === 'legacy' ? evidence.protocolLegacy(result.protocolVersion) : result.protocolVersion
  }
  if (key === 'identity') {
    const name = typeof result.serverInfo.name === 'string' ? result.serverInfo.name : ''
    const version = typeof result.serverInfo.version === 'string' ? result.serverInfo.version : ''
    if (!name) return ''
    return version ? evidence.callsItselfVersioned(name, version) : evidence.callsItself(name)
  }
  // "none needed" was asserted for every success, including the ones that succeeded
  // *because* a credential was sent — which reads to an administrator as "you can delete
  // that token reference". The probe now says which it was, rather than this guessing.
  if (key === 'authorization') return result.credentialSupplied ? evidence.credentialAccepted : evidence.credentialNone
  // The one sentence here that counts something, so it is the one that needs the locale: the
  // catalogue owns both plural forms and `Intl.PluralRules` chooses between them. The
  // `=== 1` this replaced was right in English and wrong in Portuguese, which takes the
  // singular at zero as well (PRD-27 D-19) — and the connect flow held a second copy of it.
  return t.toolCount(fmt, result.toolCount)
}

export const hostOf = (url: string): string => {
  try {
    return new URL(url).host
  } catch {
    return url
  }
}

/**
 * The ladder, from one probe answer.
 *
 * `identity` is the one stage that can pass *emptily*: a system is not obliged to say what
 * it is called, and treating silence as a failure would refuse working systems over a field
 * the specification itself marks as advisory. It passes with no detail, and the surface that
 * renders it says so in words rather than leaving a blank.
 *
 * `t` and `fmt` are parameters rather than imports so this module stays React-free and its
 * spec stays a pure one: what it needs from the active locale is the six evidence lines and
 * how to word a count.
 */
export const stagesFrom = (t: SystemsPack, fmt: Counting, result: McpProbeResult, url: string): StageView[] => {
  const host = hostOf(url)

  if (result.ok) {
    return STAGE_ORDER.map((key) => ({ key, state: 'pass' as const, detail: passDetail(t, fmt, key, result, host) }))
  }

  const { at, attempted } = result.stage ? STAGE_OF[result.stage] : { at: 'address' as StageKey, attempted: 0 }
  const failedIndex = STAGE_ORDER.indexOf(at)

  return STAGE_ORDER.map((key, index) => {
    // `attempted`, not `failedIndex`. A stage between the two was never put to the system,
    // and the only honest thing to say about a question nobody asked is that it was not
    // reached — which is what the `pending` fall-through below says.
    if (index < attempted) return { key, state: 'pass' as const, detail: passDetail(t, fmt, key, result, host) }
    if (index === failedIndex) {
      // The authorization fork gets its own sentence rather than the wire message. What
      // reaches this point is `MCP server "x" requires authorization (HTTP 401); its
      // authority is described at https://…` — four pieces of protocol vocabulary aimed at
      // whoever debugs the transport, shown to somebody who was told this screen would
      // explain itself.
      const detail = isAnswerable(result.stage) ? t.flow.evidence.signsPeopleIn : result.detail
      return {
        key,
        state: isAnswerable(result.stage) ? ('skipped' as const) : ('fail' as const),
        detail
      }
    }
    return { key, state: 'pending' as const, detail: '' }
  })
}

/**
 * Whether the flow may go on to ask for a name.
 *
 * True for a clean answer and for a system that merely wants a credential — because the
 * whole point of the authorization branch is that it is a step, not a stop.
 */
export const canContinue = (result: McpProbeResult | undefined): boolean =>
  Boolean(result && (result.ok || isAnswerable(result.stage)))

/**
 * Whether somebody has pasted a command rather than an address.
 *
 * Worth its own answer rather than a validation error, because it is the single most common
 * wrong thing to paste: a great deal of published MCP documentation gives a command line,
 * and a person following it has done nothing unreasonable. The refusal has a reason
 * attached, and the reason is not "invalid URL".
 */
export const looksLikeACommand = (value: string): boolean => {
  const trimmed = value.trim()
  if (trimmed === '' || /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)) return false
  return /^(npx|uvx|uv|node|python3?|bun|deno|docker|pipx|cmd|sh|bash)\b/i.test(trimmed) || trimmed.includes(' -')
}

/**
 * A pasted address, tidied only where tidying cannot change what it points at.
 *
 * Whitespace and a trailing slash are safe. A missing scheme is **not** guessed: `https` is
 * the overwhelmingly likely intent, and silently adding it means the person never sees that
 * they pasted something incomplete — which matters on the one day they pasted `http`.
 */
export const tidyUrl = (value: string): string => value.trim().replace(/\s+/g, '').replace(/\/+$/, '')
